/**
 * Preview mode: looking at unpublished content, deliberately and visibly.
 *
 * The CMS builds a link with `?preview=<secret>` and opens it in the panel beside the
 * editor. This module is what the site does with that: takes the token out of the address,
 * keeps it for the tab, and puts it on every content request so the API answers with
 * drafts.
 *
 * ## Why the address is rewritten
 *
 * There is no server here to set a cookie from, so the token has to arrive in the URL.
 * Leaving it there would put a bearer token in the address bar, in the browser history and
 * in the `Referer` of every outbound link. Stripping it on arrival - `replaceState`, so no
 * navigation and no reload - costs nothing and removes all three. It is the same shape
 * Next.js uses for draft mode, minus the cookie.
 *
 * `sessionStorage`, not `localStorage`: preview is a thing you are doing right now, in
 * this tab, not a preference to keep until the browser is reinstalled. Close the tab and
 * the site is a normal visitor's site again.
 *
 * ## What it does not do
 *
 * It does not decide anything. The token is checked by the API, in constant time, against
 * `PREVIEW_SECRET`; a wrong one - or a right one on an instance with no secret set - gets
 * published content like anybody else. Nothing here is a security boundary, which is why
 * it is safe for it to be this simple.
 */

const KEY = 'weco.preview';

/** Private mode and blocked storage throw rather than returning null. */
function read(): string {
  try {
    return window.sessionStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

function write(token: string) {
  try {
    window.sessionStorage.setItem(KEY, token);
  } catch {
    /* storage unavailable: preview lasts for this page rather than this tab */
  }
}

let token = '';

/**
 * Call once, as early as possible.
 *
 * Early matters: the first content request must already carry the token, or the editor
 * sees the published page flash up before the draft replaces it.
 */
export function initPreview(): string {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get('preview') ?? '';

  if (fromQuery) {
    token = fromQuery;
    write(token);

    url.searchParams.delete('preview');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    return token;
  }

  token = read();
  return token;
}

/** The token for this tab, or an empty string. */
export const previewToken = (): string => token;

export const isPreviewing = (): boolean => token !== '';

/** Leave preview mode. The page has to be reloaded to drop the drafts it already has. */
export function exitPreview() {
  token = '';
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to remove */
  }
}
